import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs'
import { EnterpriseConfig, getAllEnterpriseConfigs, getEnterpriseConfig, getEnterpriseKeyFromHostname } from '../config/enterprise.config'

const ADMIN_STORAGE_KEY = 'parkupp_admin_enterprise_filter'

/**
 * Service to manage enterprise context for admin/reporting views.
 *
 * Two contexts:
 * 1. UI Branding (domain-based): Determined by hostname, affects logos/colors/etc.
 * 2. Admin Data Filter (localStorage): Persists admin's selected enterprise for data filtering.
 *
 * On enterprise subdomains (e.g., redefine.localhost):
 * - UI shows Redefine branding
 * - Admin filter defaults to Redefine
 *
 * On default domain (localhost):
 * - UI shows ParkUpp branding
 * - Admin filter loads from localStorage (persists selection across page refreshes)
 *
 * Enterprise managers are always locked to their enterprise.
 */
@Injectable({
    providedIn: 'root',
})
export class EnterpriseContextService {
    private currentEnterpriseSubject: BehaviorSubject<EnterpriseConfig | null>
    private _isEnterpriseManager = false
    private _initialized = false
    private _hostnameKey: string | null = null

    // Observable for components to subscribe to - emits only when value changes
    currentEnterprise$: Observable<EnterpriseConfig | null>

    // Observable that emits only the key - easier to compare
    currentEnterpriseKey$: Observable<string | null>

    constructor() {
        // Detect enterprise from hostname for initial state
        this._hostnameKey = getEnterpriseKeyFromHostname(window.location.hostname)
        const isEnterpriseSubdomain = this._hostnameKey && this._hostnameKey !== 'parkupp'

        // Initial config from hostname (will be updated in initializeForAdmin if on default domain)
        const initialConfig = isEnterpriseSubdomain ? getEnterpriseConfig(this._hostnameKey!) : null

        this.currentEnterpriseSubject = new BehaviorSubject<EnterpriseConfig | null>(initialConfig)

        // Only emit when the enterprise actually changes (by key)
        this.currentEnterprise$ = this.currentEnterpriseSubject.asObservable().pipe(
            distinctUntilChanged((prev, curr) => {
                const prevKey = prev?.key ?? null
                const currKey = curr?.key ?? null
                return prevKey === currKey
            })
        )

        this.currentEnterpriseKey$ = this.currentEnterprise$.pipe(
            map((config) => config?.key ?? null),
            distinctUntilChanged()
        )
    }

    /**
     * Load enterprise key from localStorage (for admin section only)
     */
    private loadFromStorage(): string | null {
        try {
            return localStorage.getItem(ADMIN_STORAGE_KEY)
        } catch {
            return null
        }
    }

    /**
     * Save enterprise key to localStorage (for admin section only)
     */
    private saveToStorage(key: string | null): void {
        try {
            if (key) {
                localStorage.setItem(ADMIN_STORAGE_KEY, key)
            } else {
                localStorage.removeItem(ADMIN_STORAGE_KEY)
            }
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Initialize for an enterprise manager (locked to their enterprise).
     * Called by AuthenticationService after auth is complete.
     */
    initializeForEnterpriseManager(enterpriseKey: string): void {
        this._isEnterpriseManager = true
        this._initialized = true

        const config = getEnterpriseConfig(enterpriseKey)
        this.currentEnterpriseSubject.next(config)
        this.saveToStorage(enterpriseKey)
    }

    /**
     * Initialize for a regular admin.
     * Called by AuthenticationService after auth is complete.
     *
     * On enterprise subdomain: Uses that enterprise
     * On default domain: Loads saved selection from localStorage
     */
    initializeForAdmin(): void {
        this._isEnterpriseManager = false
        this._initialized = true

        const isEnterpriseSubdomain = this._hostnameKey && this._hostnameKey !== 'parkupp'

        if (isEnterpriseSubdomain) {
            // On enterprise subdomain - use hostname enterprise
            const config = getEnterpriseConfig(this._hostnameKey!)
            this.currentEnterpriseSubject.next(config)
        } else {
            // On default domain - load saved admin filter from localStorage
            const savedKey = this.loadFromStorage()
            if (savedKey) {
                const config = getEnterpriseConfig(savedKey)
                this.currentEnterpriseSubject.next(config)
            }
            // If no saved key, keep null (All Enterprises)
        }
    }

    /**
     * Get the current enterprise key for filtering queries.
     * Returns null if "All Enterprises" is selected.
     */
    get currentEnterpriseKey(): string | null {
        const config = this.currentEnterpriseSubject.value
        return config?.key ?? null
    }

    /**
     * Get the current enterprise config.
     */
    get currentEnterprise(): EnterpriseConfig | null {
        return this.currentEnterpriseSubject.value
    }

    /**
     * Check if filtering by enterprise is active.
     */
    get isFilteringByEnterprise(): boolean {
        return this.currentEnterpriseSubject.value !== null
    }

    /**
     * Check if this is an enterprise manager (locked to their enterprise).
     */
    get isEnterpriseManager(): boolean {
        return this._isEnterpriseManager
    }

    /**
     * Check if the service has been initialized.
     */
    get isInitialized(): boolean {
        return this._initialized
    }

    /**
     * Set the enterprise context for data filtering (admin only).
     * Persists to localStorage so it survives page refreshes.
     * Note: This does NOT change UI branding - that's controlled by the domain.
     * Enterprise managers cannot change their enterprise filter.
     */
    setEnterprise(enterpriseKey: string | null): void {
        if (this._isEnterpriseManager) {
            console.warn('[EnterpriseContext] Enterprise managers cannot change enterprise context')
            return
        }

        if (enterpriseKey === null) {
            this.currentEnterpriseSubject.next(null)
            this.saveToStorage(null)
        } else {
            const config = getEnterpriseConfig(enterpriseKey)
            this.currentEnterpriseSubject.next(config)
            this.saveToStorage(enterpriseKey)
        }
    }

    /**
     * Check if the user can switch enterprises.
     */
    get canSwitchEnterprise(): boolean {
        return !this._isEnterpriseManager
    }

    /**
     * Get all available enterprises for reference.
     */
    getAvailableEnterprises(): EnterpriseConfig[] {
        return getAllEnterpriseConfigs()
    }

    /**
     * Get the display name for current enterprise.
     */
    get currentEnterpriseDisplayName(): string {
        const config = this.currentEnterpriseSubject.value
        return config ? config.name : 'All Enterprises'
    }

    /**
     * Check if a specific enterprise is selected.
     */
    isEnterpriseSelected(enterpriseKey: string): boolean {
        const config = this.currentEnterpriseSubject.value
        return config?.key === enterpriseKey
    }

    /**
     * Reset to default state (for logout).
     */
    reset(): void {
        // Re-detect from hostname
        const hostnameKey = getEnterpriseKeyFromHostname(window.location.hostname)
        const isEnterpriseSubdomain = hostnameKey && hostnameKey !== 'parkupp'
        const config = isEnterpriseSubdomain ? getEnterpriseConfig(hostnameKey!) : null

        this.currentEnterpriseSubject.next(config)
        this._initialized = false
        this._isEnterpriseManager = false
        this.saveToStorage(null) // Clear saved admin filter on logout
    }
}
